import Select from 'src/components/ui/BrandSelect';
import { format } from 'date-fns';
import { connect } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, SquarePlus, Download, Check, X } from 'lucide-react';
import Breadcrumb from '../../Common/Breadcrumb';
import { uuidValidateV4 } from '../../utils/GlobalFunctions';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Card } from '../../ui/card';
import { Spinner } from '../../ui/spinner';

const TemplateVersioning = ({ templates, showLoader, toggleModal, getTemplates, toggleTemplate, generateNewTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const versions = useMemo(
    () => templates?.find((t) => t.value === selectedTemplate)?.versions,
    [templates, selectedTemplate],
  );

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  return (
    <TooltipProvider>
      <Breadcrumb items={[{ label: 'Template Versioning', link: '/admin/templates' }]} />

      <div className='card'>
        <div className='card-body'>
          <div className='mb-4'>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '300px' }}>
                <div style={{ flexGrow: 1, marginRight: '10px' }}>
                  <Select
                    placeholder='Select Template'
                    onChange={(selected) => setSelectedTemplate(selected.value)}
                    options={templates}
                  />
                </div>
                <Button disabled={showLoader} onClick={() => getTemplates()}>
                  <RefreshCw size={16} strokeWidth={1.5} />
                </Button>
              </div>

              {selectedTemplate && (
                <Button
                  disabled={showLoader}
                  onClick={() => generateNewTemplate(selectedTemplate)}>
                  <SquarePlus size={16} strokeWidth={1.5} className='mr-2' />
                  Generate New Version
                </Button>
              )}
            </div>
          </div>

          {showLoader && <div className='mt-6'><Spinner /></div>}
          {selectedTemplate && !showLoader && (
            <Card className='rounded-lg mt-6'>
              <div className='table-responsive'>
                <table className='app-table w-full text-center'>
                  <thead>
                    <tr>
                      <th className='px-4 py-3 align-middle'>Version</th>
                      <th className='px-4 py-3 align-middle'>Created At</th>
                      <th className='px-4 py-3 align-middle'>Created By</th>
                      <th className='px-4 py-3 align-middle'>Status</th>
                      <th className='px-4 py-3 align-middle'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions?.length === 0 && (
                      <tr>
                        <td colSpan='5' className='px-4 py-6 text-center align-middle text-muted-foreground'>
                          No versions found!
                        </td>
                      </tr>
                    )}
                    {versions?.map((version) => (
                      <tr key={version.id}>
                        <td className='px-4 py-3 align-middle'>{version.version}</td>
                        <td className='px-4 py-3 align-middle'>
                          {format(new Date(version.createdAt), 'HH:mm dd/MM/yyyy')}
                        </td>
                        {!uuidValidateV4(version.createdBy) && (
                          <td className='px-4 py-3 align-middle'>{version.createdBy}</td>
                        )}
                        {uuidValidateV4(version.createdBy) && (
                          <td className='px-4 py-3 align-middle'>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>Unknown</span>
                              </TooltipTrigger>
                              <TooltipContent>{version.createdBy}</TooltipContent>
                            </Tooltip>
                          </td>
                        )}
                        <td className='px-4 py-3 align-middle'>
                          <span className={`badge ${version.active ? 'bg-success' : 'bg-secondary'}`}>
                            {version.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className='px-4 py-3 align-middle'>
                          <div className='flex flex-wrap justify-center items-center gap-2'>
                            <a target='_blank' href={version.downloadLink}>
                              <Button variant='link' size='sm' className='p-0 h-auto' title='Download'>
                                <Download size={18} strokeWidth={1.5} />
                              </Button>
                            </a>
                            <Button
                              variant='link'
                              size='sm'
                              className='p-0 h-auto'
                              onClick={() => toggleTemplate(version)}
                              title={version.active ? 'Deactivate' : 'Activate'}>
                              {!version.active && <Check size={18} strokeWidth={1.5} />}
                              {version.active && <X size={18} strokeWidth={1.5} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => ({
  showLoader: state?.showLoader,
  templates: state?.admin?.adminPortal?.templates || [],
});
const mapDispatchToProps = (dispatch) => ({
  getTemplates: () => dispatch(ADMINACTIONS.getTemplates()),
  toggleTemplate: (template) => dispatch(ADMINACTIONS.toggleTemplate(template)),
  generateNewTemplate: (templateType) => dispatch(ADMINACTIONS.generateNewTemplate(templateType)),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TemplateVersioning);
